/**
 * Financial Details Modal Component
 * BEYOND WORLD-CLASS: Dedicated modal for financial details (mobile-only)
 * Progressive disclosure with accordions, "why" explanations, and strict gesture contract
 */

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { resolveFinancialDiscountLineItems } from '../../utils/financialDiscounts.js';
import { getReservations } from '../../services/ownerApi.js';
import { SkeletonCard, SkeletonText } from '../shared/SkeletonLoader.jsx';
import FinancialCompareDrawer from './FinancialCompareDrawer.jsx';

// FIX: Move ENABLE_COMPARE_DRAWER to module level to avoid re-evaluation and ensure consistency
const ENABLE_COMPARE_DRAWER = import.meta.env.VITE_ENABLE_FINANCIAL_COMPARE_DRAWER === 'true';

// Haptic feedback helper (safe fallback for unsupported browsers)
const triggerHaptic = (type = 'light') => {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try {
    // Light: 10ms, Medium: 20ms, Heavy: 30ms
    const duration = type === 'light' ? 10 : type === 'medium' ? 20 : 30;
    navigator.vibrate(duration);
  } catch (e) {
    // Silently fail if vibration not supported
  }
};

// Format currency helper
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

function hasNonZeroAmount(value) {
  if (value == null) return false;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) && Math.abs(n) > 0.005;
}

/** Guest Payment breakdown rows — persisted-only, matches desktop BookingFinancialFolio line items. */
function GuestPaymentBreakdownRows({ safeGuestPaid, guestTotal, styles }) {
  const addOnFees = safeGuestPaid.add_on_fees || [];
  return (
    <>
      <div style={styles.detailRow}>
        <span style={styles.detailLabel}>Base Rate</span>
        <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_base_rate)}</span>
      </div>
      {resolveFinancialDiscountLineItems({ discountLineItems: safeGuestPaid.discount_line_items, discountsTotal: safeGuestPaid.guest_discounts_total, legacyCouponDiscount: safeGuestPaid.guest_coupon_discount, legacyWeeklyDiscount: safeGuestPaid.guest_weekly_discount }).map(item => (
        <div key={item.key} style={styles.detailRow}><span style={styles.detailLabel}>{item.label}</span><span style={styles.detailValue}>{formatCurrency(item.amount)}</span></div>
      ))}
      <div style={styles.detailRow}>
        <span style={styles.detailLabel}>Cleaning Fee</span>
        <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_cleaning_fee)}</span>
      </div>
      {addOnFees.map(({ key, label, amount }) => (
        <div key={key} style={styles.detailRow}>
          <span style={styles.detailLabel}>{label}</span>
          <span style={styles.detailValue}>{formatCurrency(amount)}</span>
        </div>
      ))}
      <div style={styles.detailRow}>
        <span style={styles.detailLabel}>Tourist Tax</span>
        <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_tourist_tax)}</span>
      </div>
      {hasNonZeroAmount(safeGuestPaid.guest_service_fee) && (
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Guest Service Fee</span>
          <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_service_fee)}</span>
        </div>
      )}
      {hasNonZeroAmount(safeGuestPaid.guest_channel_fee) && (
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>Channel Fees</span>
          <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_channel_fee)}</span>
        </div>
      )}
      <div style={styles.detailRow}>
        <span style={styles.detailLabel}>Total</span>
        <span style={styles.detailValue}>{formatCurrency(guestTotal)}</span>
      </div>
    </>
  );
}

// Accordion section component
function AccordionSection({ title, isExpanded, onToggle, children, dataKey }) {
  const accordionId = dataKey || title.toLowerCase().replace(/\s+/g, '-');
  return (
    <div style={styles.accordionSection} data-financial-accordion={accordionId}>
      <button
        onClick={onToggle}
        style={styles.accordionHeader}
        aria-expanded={isExpanded}
        aria-controls={`accordion-${accordionId}`}
      >
        <span style={styles.accordionTitle}>{title}</span>
        <span
          style={{
            ...styles.accordionIcon,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </button>
      {isExpanded && (
        <div
          id={`accordion-${accordionId}`}
          style={styles.accordionContent}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default function FinancialDetailsModal({ isOpen, onClose, booking, isMobile = false, onRetryFetch, embedInSheet = false }) {
  const [expandedSections, setExpandedSections] = useState({
    baseRate: false,
    fees: false,
    taxes: false,
    adjustments: false,
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  // Mobile redesign: Summary | Breakdown tabs (mobile only)
  const [mobileTab, setMobileTab] = useState('summary');

  // Phase 4: Compare drawer state
  const [compareDrawerOpen, setCompareDrawerOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState(null);
  // FIX: ENABLE_COMPARE_DRAWER moved to module level (line 13)

  // Persisted-only: same source of truth as desktop BookingFinancialFolio (no canonical fallback for amounts)
  const persistedSummary = booking?.financials_persisted_summary ?? null;
  const guestPaidSource = persistedSummary?.guest_paid ?? null;
  const ownerPayoutSource = persistedSummary?.owner_payout ?? null;
  const ownerRevenue = persistedSummary?.owner_revenue?.amount ?? null;
  const ownerTouristTaxDisplay = persistedSummary?.tourist_tax?.owner_remits
    ? (persistedSummary.tourist_tax.tourist_tax_to_subtract ?? null)
    : null;

  const hasCriticalData = persistedSummary && (
    guestPaidSource?.guest_total_paid != null ||
    ownerPayoutSource?.owner_payout_total != null
  );

  const isPartialData = booking && !hasCriticalData;

  const safeGuestPaid = {
    guest_base_rate: guestPaidSource?.guest_base_rate ?? null,
    guest_cleaning_fee: guestPaidSource?.guest_cleaning_fee ?? null,
    guest_tourist_tax: guestPaidSource?.guest_tourist_tax ?? null,
    guest_coupon_discount: guestPaidSource?.guest_coupon_discount ?? null,
    guest_weekly_discount: guestPaidSource?.guest_weekly_discount ?? null,
    discount_line_items: guestPaidSource?.discount_line_items,
    guest_discounts_total: guestPaidSource?.guest_discounts_total,
    guest_channel_fee: guestPaidSource?.guest_channel_fee ?? null,
    guest_service_fee: guestPaidSource?.guest_service_fee ?? null,
    add_on_fees: guestPaidSource?.add_on_fees ?? [],
    guest_total_paid: guestPaidSource?.guest_total_paid ?? null,
  };

  const safeOwnerPayout = {
    owner_base_rate: ownerPayoutSource?.owner_base_rate ?? null,
    owner_discount_status: ownerPayoutSource?.owner_discount_status,
    owner_cleaning_fee: ownerPayoutSource?.owner_cleaning_fee ?? null,
    owner_channel_fees: ownerPayoutSource?.owner_channel_fees ?? null,
    owner_adjustments: ownerPayoutSource?.owner_adjustments ?? null,
    owner_adjustments_total: ownerPayoutSource?.owner_adjustments_total ?? ownerPayoutSource?.owner_adjustments ?? null,
    owner_adjustments_breakdown: ownerPayoutSource?.owner_adjustments_breakdown || [],
    owner_payout_total: ownerPayoutSource?.owner_payout_total ?? null,
    owner_payout_status: ownerPayoutSource?.owner_payout_status ?? null,
    owner_tourist_tax: ownerTouristTaxDisplay,
  };

  // Totals (must be before embedInSheet path so renderMobileFinancialContent can use them)
  const guestTotal = safeGuestPaid.guest_total_paid;
  const ownerTotal = safeOwnerPayout.owner_payout_total;
  const taxes = safeGuestPaid.guest_tourist_tax;
  const platformFees = safeOwnerPayout.owner_channel_fees;

  // Toggle accordion section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
    triggerHaptic('light');
  };
  
  // PREMIUM: Retry fetch financial data
  const handleRetry = async () => {
    if (!booking?.reservation_id || !onRetryFetch) return;
    
    setIsRetrying(true);
    triggerHaptic('light');
    
    try {
      await onRetryFetch(booking.reservation_id);
      // Wait a moment for state to update
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('[FinancialDetailsModal] Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };
  
  // Phase 4: Fetch persisted data when drawer opens (PERSISTED-ONLY: No compare endpoint)
  useEffect(() => {
    if (!compareDrawerOpen || !ENABLE_COMPARE_DRAWER || !booking?.reservation_id) {
      return;
    }
    
    const fetchPersistedData = async () => {
      setCompareLoading(true);
      setCompareError(null);
      
      try {
        // PERSISTED-ONLY: Call persisted endpoint (not compare)
        // RUNTIME GUARD: Warn if compare endpoint is accidentally called
        const endpoint = `/api/v1/owner/reservations/${booking.reservation_id}/financials/persisted`;
        if (endpoint.includes('/financials/compare')) {
          console.error('[PERSISTED DRAWER] BLOCKED: Attempted to call compare endpoint!', { endpoint });
          throw new Error('Compare endpoint is disabled - use /financials/persisted only');
        }
        
        const response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        setCompareData(data.data || data);
      } catch (error) {
        console.error('[FinancialDetailsModal] Failed to fetch persisted data:', error);
        setCompareError(error.message || 'Failed to load persisted data');
      } finally {
        setCompareLoading(false);
      }
    };
    
    fetchPersistedData();
  }, [compareDrawerOpen, ENABLE_COMPARE_DRAWER, booking?.reservation_id]);

  // Body scroll lock (bulletproof iOS Safari approach)
  useEffect(() => {
    if (!isOpen || !isMobile) return;

    // Capture scroll position before locking
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    
    // Calculate scrollbar width to prevent layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    // Lock body scroll (bulletproof for iOS Safari)
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = `-${scrollX}px`;
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Lock documentElement scroll (backup for iOS)
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.width = '100%';
    document.documentElement.style.height = '100%';
    
    // Prevent scrollbar shift (desktop)
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    // Prevent touchmove on body (iOS Safari extra protection)
    const preventTouchMove = (e) => {
      // Allow scrolling within the modal
      const scrollContainer = modalRef.current?.querySelector('[data-financial-modal-scroll="true"]');
      if (scrollContainer && scrollContainer.contains(e.target)) {
        return; // Allow touch events inside modal scroll container
      }
      e.preventDefault();
    };
    
    document.body.addEventListener('touchmove', preventTouchMove, { passive: false });

    // Focus trap
    const focusableElements = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements?.[0];
    const lastFocusable = focusableElements?.[focusableElements.length - 1];

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
        triggerHaptic('light');
      } else if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable?.focus();
            e.preventDefault();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusable?.focus();

    return () => {
      // Restore scroll position and styles
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      
      document.documentElement.style.overflow = '';
      document.documentElement.style.position = '';
      document.documentElement.style.width = '';
      document.documentElement.style.height = '';
      
      // Restore scroll position
      window.scrollTo(scrollX, scrollY);
      
      // Remove touchmove prevention
      document.body.removeEventListener('touchmove', preventTouchMove);
      
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile, onClose]);

  // Haptic on open/close
  useEffect(() => {
    if (isOpen && isMobile) {
      triggerHaptic('light');
    }
  }, [isOpen, isMobile]);

  // embedInSheet: render only tab bar + content for inlining in reservation sheet (mobile direct view)
  const renderMobileFinancialContent = () => {
    if ((!booking || isRetrying) && !isPartialData) {
      return (
        <div style={styles.skeletonContainer}>
          <SkeletonCard />
          <SkeletonText lines={4} width="100%" />
          <SkeletonCard />
          <SkeletonText lines={3} width="80%" />
        </div>
      );
    }
    if (mobileTab === 'summary') {
      return (
        <>
          {isPartialData && (
            <div style={styles.partialNotice} data-financial-partial-notice="true">
              <div style={styles.partialNoticeHeader}>
                <span style={styles.partialNoticeIcon}>⚠️</span>
                <span style={styles.partialNoticeTitle}>Metrics temporarily unavailable</span>
              </div>
              <p style={styles.partialNoticeText}>Some financial details couldn't be loaded. Your booking information is still available.</p>
              {onRetryFetch && (
                <button onClick={handleRetry} disabled={isRetrying} style={styles.retryButton} aria-label="Retry">{isRetrying ? 'Retrying...' : 'Retry'}</button>
              )}
            </div>
          )}
          <div style={styles.mobileSummaryCards}>
            <div style={styles.mobileSummaryCard} data-financial-summary-item="guestPaid">
              <div style={styles.mobileSummaryCardLabel}>Guest Payment</div>
              <div style={styles.mobileSummaryCardValue}>{formatCurrency(guestTotal)}</div>
              <div style={styles.mobileSummaryCardWhy}>Total collected from guest</div>
            </div>
            <div style={styles.mobileSummaryCard} data-financial-summary-item="ownerPayout">
              <div style={styles.mobileSummaryCardLabel}>Owner Payout</div>
              <div style={styles.mobileSummaryCardValue}>{formatCurrency(ownerTotal)}</div>
              <div style={styles.mobileSummaryCardWhy}>Paid to you before deductions</div>
            </div>
            <div style={styles.mobileSummaryCard} data-financial-summary-item="ownerRevenue">
              <div style={styles.mobileSummaryCardLabel}>Owner Revenue</div>
              <div style={styles.mobileSummaryCardValue}>{formatCurrency(ownerRevenue)}</div>
              <div style={styles.mobileSummaryCardWhy}>What you receive after fees and taxes</div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        {isPartialData && (
          <div style={styles.partialNotice} data-financial-partial-notice="true">
            <div style={styles.partialNoticeHeader}>
              <span style={styles.partialNoticeIcon}>⚠️</span>
              <span style={styles.partialNoticeTitle}>Metrics temporarily unavailable</span>
            </div>
          </div>
        )}
        <div style={styles.mobileBreakdownSection}>
          <div style={styles.mobileBreakdownSectionTitle}>Guest Payment</div>
          <div style={styles.mobileBreakdownBlock}>
            <GuestPaymentBreakdownRows safeGuestPaid={safeGuestPaid} guestTotal={guestTotal} styles={styles} />
          </div>
        </div>
        <div style={styles.mobileBreakdownSection}>
          <div style={styles.mobileBreakdownSectionTitle}>Owner Payout</div>
          <div style={styles.mobileBreakdownBlock}>
            {['unresolved', 'source_unavailable'].includes(safeOwnerPayout.owner_discount_status) && <p role="status">Discount details need review. Net base rate is unavailable.</p>}
            <div style={styles.detailRow}><span style={styles.detailLabel}>Base Rate</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_base_rate)}</span></div>
            {resolveFinancialDiscountLineItems({ discountLineItems: safeGuestPaid.discount_line_items, discountsTotal: safeGuestPaid.guest_discounts_total, legacyCouponDiscount: safeGuestPaid.guest_coupon_discount, legacyWeeklyDiscount: safeGuestPaid.guest_weekly_discount }).map(item => (
              <div key={item.key} style={styles.detailRow}><span style={styles.detailLabel}>{item.label}</span><span style={styles.detailValue}>{formatCurrency(item.amount)}</span></div>
            ))}
            <div style={styles.detailRow}><span style={styles.detailLabel}>Cleaning Fee</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_cleaning_fee)}</span></div>
            <div style={styles.detailRow}><span style={styles.detailLabel}>Tourist Tax</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_tourist_tax)}</span></div>
            <div style={styles.detailRow}><span style={styles.detailLabel}>Platform Fees</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_channel_fees)}</span></div>
            {(safeOwnerPayout.owner_adjustments_total !== null && safeOwnerPayout.owner_adjustments_total !== undefined) && (
              <div style={styles.detailRow}><span style={styles.detailLabel}>Adjustments</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_adjustments_total)}</span></div>
            )}
            <div style={styles.detailRow}><span style={styles.detailLabel}>Payout Total</span><span style={styles.detailValue}>{formatCurrency(ownerTotal)}</span></div>
          </div>
        </div>
        {(ownerRevenue !== null && ownerRevenue !== undefined) && (
          <div style={styles.mobileBreakdownSection}>
            <div style={styles.mobileBreakdownSectionTitle}>Owner Revenue</div>
            <div style={styles.mobileBreakdownBlock}>
              <div style={styles.detailRow}><span style={styles.detailLabel}>Amount</span><span style={styles.detailValue}>{formatCurrency(ownerRevenue)}</span></div>
            </div>
          </div>
        )}
      </>
    );
  };

  if (embedInSheet && isMobile) {
    return (
      <>
        <div style={styles.mobileTabBar}>
          <button
            type="button"
            style={{ ...styles.mobileTab, ...(mobileTab === 'summary' ? styles.mobileTabActive : {}) }}
            onClick={() => { setMobileTab('summary'); triggerHaptic('light'); }}
            aria-selected={mobileTab === 'summary'}
          >
            Summary
          </button>
          <button
            type="button"
            style={{ ...styles.mobileTab, ...(mobileTab === 'breakdown' ? styles.mobileTabActive : {}) }}
            onClick={() => { setMobileTab('breakdown'); triggerHaptic('light'); }}
            aria-selected={mobileTab === 'breakdown'}
          >
            Breakdown
          </button>
        </div>
        <div style={styles.embedContent} data-financial-modal-content="true" data-financial-modal-scroll="true">
          {renderMobileFinancialContent()}
        </div>
      </>
    );
  }

  // Phase 4: Allow modal to render on desktop if compare drawer is enabled (for testing)
  if (!isOpen) return null;
  if (!isMobile && !ENABLE_COMPARE_DRAWER) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
      triggerHaptic('light');
    }
  };

  // Compute backdrop style with reduced motion check
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const backdropStyle = {
    ...styles.backdrop,
    backdropFilter: prefersReducedMotion ? 'none' : 'blur(4px)',
    WebkitBackdropFilter: prefersReducedMotion ? 'none' : 'blur(4px)',
  };

  // Milestone A: 100dvh with 100vh fallback for full-screen modal
  const modalHeight = typeof CSS !== 'undefined' && CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh';
  const modalStyle = { ...styles.modal, height: modalHeight };

  return createPortal(
    <div
      data-financial-modal-backdrop="true"
      style={backdropStyle}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="financial-modal-title"
    >
      <div
        ref={modalRef}
        data-financial-modal="true"
        data-financial-modal-root="true"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* FULLSCREEN: Header with clear "Financials" title and close affordances */}
        <div style={styles.header}>
          <h2 id="financial-modal-title" style={styles.title}>Financials</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Phase 4: Compare button (feature flag gated, desktop) */}
            {ENABLE_COMPARE_DRAWER && !isMobile && (
              <button
                onClick={() => {
                  setCompareDrawerOpen(!compareDrawerOpen);
                  triggerHaptic('light');
                }}
                style={{
                  ...styles.closeButton,
                  fontSize: '16px',
                  padding: '8px',
                  minWidth: 'auto',
                }}
                aria-label="Toggle compare drawer"
                type="button"
                data-financial-compare="true"
                title="View persisted financials reference"
              >
                ⚖️
              </button>
            )}
            {/* Mobile: X + Done for unmistakable close */}
            {isMobile && (
              <>
                <button
                  onClick={() => {
                    onClose();
                    triggerHaptic('light');
                  }}
                  style={styles.closeButton}
                  aria-label="Close financial details"
                  type="button"
                  data-financial-close="true"
                >
                  ×
                </button>
                <button
                  ref={firstFocusableRef}
                  onClick={() => {
                    onClose();
                    triggerHaptic('light');
                  }}
                  style={styles.doneButton}
                  aria-label="Close financial details"
                  type="button"
                  data-financial-close-done="true"
                >
                  Done
                </button>
              </>
            )}
            {/* Desktop: Keep × icon only */}
            {!isMobile && (
              <button
                ref={firstFocusableRef}
                onClick={() => {
                  onClose();
                  triggerHaptic('light');
                }}
                style={styles.closeButton}
                aria-label="Close financial details"
                type="button"
                data-financial-close="true"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Mobile: Tab bar (Summary | Breakdown) */}
        {isMobile && (
          <div style={styles.mobileTabBar}>
            <button
              type="button"
              style={{
                ...styles.mobileTab,
                ...(mobileTab === 'summary' ? styles.mobileTabActive : {}),
              }}
              onClick={() => { setMobileTab('summary'); triggerHaptic('light'); }}
              aria-selected={mobileTab === 'summary'}
            >
              Summary
            </button>
            <button
              type="button"
              style={{
                ...styles.mobileTab,
                ...(mobileTab === 'breakdown' ? styles.mobileTabActive : {}),
              }}
              onClick={() => { setMobileTab('breakdown'); triggerHaptic('light'); }}
              aria-selected={mobileTab === 'breakdown'}
            >
              Breakdown
            </button>
          </div>
        )}

        {/* FULLSCREEN: Content - Scrollable */}
        <div style={styles.content} data-financial-modal-content="true" data-financial-modal-scroll="true">
          {/* B4: Skeleton loading state */}
          {(!booking || isRetrying) && !isPartialData ? (
            <div style={styles.skeletonContainer}>
              <SkeletonCard />
              <SkeletonText lines={4} width="100%" />
              <SkeletonCard />
              <SkeletonText lines={3} width="80%" />
            </div>
          ) : isMobile && mobileTab === 'summary' ? (
            /* Mobile Summary tab: 3 hero cards */
            <>
              {isPartialData && (
                <div style={styles.partialNotice} data-financial-partial-notice="true">
                  <div style={styles.partialNoticeHeader}>
                    <span style={styles.partialNoticeIcon}>⚠️</span>
                    <span style={styles.partialNoticeTitle}>Metrics temporarily unavailable</span>
                  </div>
                  <p style={styles.partialNoticeText}>
                    Some financial details couldn't be loaded. Your booking information is still available.
                  </p>
                  {onRetryFetch && (
                    <button onClick={handleRetry} disabled={isRetrying} style={styles.retryButton} aria-label="Retry">
                      {isRetrying ? 'Retrying...' : 'Retry'}
                    </button>
                  )}
                </div>
              )}
              <div style={styles.mobileSummaryCards}>
                <div style={styles.mobileSummaryCard} data-financial-summary-item="guestPaid">
                  <div style={styles.mobileSummaryCardLabel}>Guest Payment</div>
                  <div style={styles.mobileSummaryCardValue}>{formatCurrency(guestTotal)}</div>
                  <div style={styles.mobileSummaryCardWhy}>Total collected from guest</div>
                </div>
                <div style={styles.mobileSummaryCard} data-financial-summary-item="ownerPayout">
                  <div style={styles.mobileSummaryCardLabel}>Owner Payout</div>
                  <div style={styles.mobileSummaryCardValue}>{formatCurrency(ownerTotal)}</div>
                  <div style={styles.mobileSummaryCardWhy}>Paid to you before deductions</div>
                </div>
                <div style={styles.mobileSummaryCard} data-financial-summary-item="ownerRevenue">
                  <div style={styles.mobileSummaryCardLabel}>Owner Revenue</div>
                  <div style={styles.mobileSummaryCardValue}>{formatCurrency(ownerRevenue)}</div>
                  <div style={styles.mobileSummaryCardWhy}>What you receive after fees and taxes</div>
                </div>
              </div>
            </>
          ) : isMobile && mobileTab === 'breakdown' ? (
            /* Mobile Breakdown tab: one scroll, Guest then Owner sections */
            <>
              {isPartialData && (
                <div style={styles.partialNotice} data-financial-partial-notice="true">
                  <div style={styles.partialNoticeHeader}>
                    <span style={styles.partialNoticeIcon}>⚠️</span>
                    <span style={styles.partialNoticeTitle}>Metrics temporarily unavailable</span>
                  </div>
                </div>
              )}
              <div style={styles.mobileBreakdownSection}>
                <div style={styles.mobileBreakdownSectionTitle}>Guest Payment</div>
                <div style={styles.mobileBreakdownBlock}>
                  <GuestPaymentBreakdownRows safeGuestPaid={safeGuestPaid} guestTotal={guestTotal} styles={styles} />
                </div>
              </div>
              <div style={styles.mobileBreakdownSection}>
                <div style={styles.mobileBreakdownSectionTitle}>Owner Payout</div>
                <div style={styles.mobileBreakdownBlock}>
                  {['unresolved', 'source_unavailable'].includes(safeOwnerPayout.owner_discount_status) && <p role="status">Discount details need review. Net base rate is unavailable.</p>}
            <div style={styles.detailRow}><span style={styles.detailLabel}>Base Rate</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_base_rate)}</span></div>
            {resolveFinancialDiscountLineItems({ discountLineItems: safeGuestPaid.discount_line_items, discountsTotal: safeGuestPaid.guest_discounts_total, legacyCouponDiscount: safeGuestPaid.guest_coupon_discount, legacyWeeklyDiscount: safeGuestPaid.guest_weekly_discount }).map(item => (
              <div key={item.key} style={styles.detailRow}><span style={styles.detailLabel}>{item.label}</span><span style={styles.detailValue}>{formatCurrency(item.amount)}</span></div>
            ))}
                  <div style={styles.detailRow}><span style={styles.detailLabel}>Cleaning Fee</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_cleaning_fee)}</span></div>
                  <div style={styles.detailRow}><span style={styles.detailLabel}>Tourist Tax</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_tourist_tax)}</span></div>
                  <div style={styles.detailRow}><span style={styles.detailLabel}>Platform Fees</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_channel_fees)}</span></div>
                  {(safeOwnerPayout.owner_adjustments_total !== null && safeOwnerPayout.owner_adjustments_total !== undefined) && (
                    <div style={styles.detailRow}><span style={styles.detailLabel}>Adjustments</span><span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_adjustments_total)}</span></div>
                  )}
                  <div style={styles.detailRow}><span style={styles.detailLabel}>Payout Total</span><span style={styles.detailValue}>{formatCurrency(ownerTotal)}</span></div>
                </div>
              </div>
              {(ownerRevenue !== null && ownerRevenue !== undefined) && (
                <div style={styles.mobileBreakdownSection}>
                  <div style={styles.mobileBreakdownSectionTitle}>Owner Revenue</div>
                  <div style={styles.mobileBreakdownBlock}>
                    <div style={styles.detailRow}><span style={styles.detailLabel}>Amount</span><span style={styles.detailValue}>{formatCurrency(ownerRevenue)}</span></div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
          {/* PREMIUM: Trust-grade partial data notice (desktop / non-mobile) */}
          {isPartialData && (
            <div style={styles.partialNotice} data-financial-partial-notice="true">
              <div style={styles.partialNoticeHeader}>
                <span style={styles.partialNoticeIcon}>⚠️</span>
                <span style={styles.partialNoticeTitle}>Metrics temporarily unavailable</span>
              </div>
              <p style={styles.partialNoticeText}>
                Some financial details couldn't be loaded. Your booking information is still available.
              </p>
              {onRetryFetch && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying}
                  style={styles.retryButton}
                  data-financial-retry="true"
                  aria-label="Retry loading financial details"
                >
                  {isRetrying ? 'Retrying...' : 'Retry'}
                </button>
              )}
            </div>
          )}

          {/* Desktop: Summary 2x2 grid + accordions (unchanged) */}
          <div style={styles.summaryGrid} data-financial-summary-grid="true">
            <div style={styles.summaryGridItem} data-financial-summary-item="guestPaid">
              <div style={styles.summaryGridLabel}>Guest Paid</div>
              <div style={styles.summaryGridValue}>{formatCurrency(guestTotal)}</div>
              <div style={styles.summaryGridWhy}>Total amount collected from guest</div>
            </div>
            <div style={styles.summaryGridItem} data-financial-summary-item="ownerPayout">
              <div style={styles.summaryGridLabel}>Owner Payout</div>
              <div style={styles.summaryGridValue}>{formatCurrency(ownerTotal)}</div>
              <div style={styles.summaryGridWhy}>What you receive after fees and adjustments</div>
            </div>
            <div style={styles.summaryGridItem} data-financial-summary-item="taxes">
              <div style={styles.summaryGridLabel}>Taxes</div>
              <div style={styles.summaryGridValue}>{formatCurrency(taxes)}</div>
              <div style={styles.summaryGridWhy}>Tourist tax remitted to local authorities</div>
            </div>
            <div style={styles.summaryGridItem} data-financial-summary-item="platformFees">
              <div style={styles.summaryGridLabel}>Platform Fees</div>
              <div style={styles.summaryGridValue}>{formatCurrency(platformFees)}</div>
              <div style={styles.summaryGridWhy}>Service fees charged by booking platform</div>
            </div>
          </div>

          {/* Accordion Sections (desktop) */}
          <AccordionSection
            title="Base Rate"
            isExpanded={expandedSections.baseRate}
            onToggle={() => toggleSection('baseRate')}
            dataKey="baseRate"
          >
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Guest Base Rate</span>
              <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_base_rate)}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Owner Base Rate</span>
              <span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_base_rate)}</span>
            </div>
          </AccordionSection>

          <AccordionSection
            title="Fees"
            isExpanded={expandedSections.fees}
            onToggle={() => toggleSection('fees')}
            dataKey="fees"
          >
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Cleaning Fee (Guest)</span>
              <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_cleaning_fee)}</span>
            </div>
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Cleaning Fee (Owner)</span>
              <span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_cleaning_fee)}</span>
            </div>
            {safeOwnerPayout.owner_channel_fees !== null && (
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Platform Fees</span>
                <span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_channel_fees)}</span>
              </div>
            )}
          </AccordionSection>

          <AccordionSection
            title="Taxes"
            isExpanded={expandedSections.taxes}
            onToggle={() => toggleSection('taxes')}
            dataKey="taxes"
          >
            <div style={styles.detailRow}>
              <span style={styles.detailLabel}>Tourist Tax (Guest)</span>
              <span style={styles.detailValue}>{formatCurrency(safeGuestPaid.guest_tourist_tax)}</span>
            </div>
            {safeOwnerPayout.owner_tourist_tax !== null && (
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Tourist Tax (Owner)</span>
                <span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_tourist_tax)}</span>
              </div>
            )}
          </AccordionSection>

          {safeOwnerPayout.owner_adjustments !== null && (
            <AccordionSection
              title="Adjustments"
              isExpanded={expandedSections.adjustments}
              onToggle={() => toggleSection('adjustments')}
              dataKey="adjustments"
            >
              <div style={styles.detailRow}>
                <span style={styles.detailLabel}>Total Adjustments</span>
                <span style={styles.detailValue}>{formatCurrency(safeOwnerPayout.owner_adjustments_total)}</span>
              </div>
              {safeOwnerPayout.owner_adjustments_breakdown?.length > 0 && (
                <div style={styles.breakdownSection}>
                  {safeOwnerPayout.owner_adjustments_breakdown.map((item, idx) => (
                    <div key={idx} style={styles.detailRow}>
                      <span style={styles.detailLabel}>{item.label || item.type || 'Adjustment'}</span>
                      <span style={styles.detailValue}>{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </AccordionSection>
          )}

          {/* Fallback if no data */}
          {!persistedSummary && (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>Financial details not available for this booking.</p>
            </div>
          )}
            </>
          )}
        </div>
      </div>
      
      {/* Phase 4: Compare Drawer */}
      {/* FIX: Ensure FinancialCompareDrawer is defined before rendering to prevent React error #310 */}
      {ENABLE_COMPARE_DRAWER && FinancialCompareDrawer && (
        <FinancialCompareDrawer
          isOpen={compareDrawerOpen}
          onClose={() => {
            setCompareDrawerOpen(false);
            triggerHaptic('light');
          }}
          booking={booking}
          persistedData={compareData}
          isLoading={compareLoading}
          error={compareError}
        />
      )}
    </div>,
    document.body
  );
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0, // top: 0, left: 0, right: 0, bottom: 0 - bulletproof positioning
    backgroundColor: 'rgba(0, 0, 0, 0.8)', // Stronger dim for clear modal distinction
    zIndex: 10000, // Above app shell (9999) so modal header/close visible on mobile
    display: 'flex',
    flexDirection: 'column', // FULLSCREEN: Stack vertically for full-height modal
    overflow: 'hidden', // Prevent backdrop from scrolling
  },
  modal: {
    position: 'fixed', // Explicit positioning for bulletproof layout
    inset: 0, // top: 0, left: 0, right: 0, bottom: 0
    backgroundColor: 'var(--color-bg-primary)',
    width: '100vw', // Explicit viewport width
    height: '100dvh', // FULLSCREEN: Use 100dvh for full viewport height
    maxHeight: '100vh', // Fallback for browsers that don't support dvh
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevent modal root from scrolling
    overflowX: 'hidden', // Explicitly prevent horizontal scroll
    paddingBottom: 'env(safe-area-inset-bottom, 0)', // FULLSCREEN: Safe area for iOS notch/home indicator
    paddingTop: 0, // No top padding (handled by header)
    paddingLeft: 0,
    paddingRight: 0,
    /* Phase 9: Premium brand-tinted shadow for modal elevation */
    boxShadow: '0 8px 24px rgba(37, 72, 96, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-4) var(--spacing-5)', // FULLSCREEN: Consistent padding
    paddingTop: 'calc(var(--spacing-4) + env(safe-area-inset-top, 0))', // FULLSCREEN: Safe area for iOS notch
    flexShrink: 0,
    backgroundColor: 'var(--color-bg-primary)',
    borderBottom: '1px solid var(--color-border-light)', // FULLSCREEN: Clear header separation
    position: 'sticky', // FULLSCREEN: Sticky header so it stays visible when scrolling
    top: 0,
    zIndex: 10, // Ensure header stays above content when scrolling
  },
  headerDivider: {
    height: '1px',
    backgroundColor: 'var(--color-border-light)',
    margin: 0,
    flexShrink: 0,
  },
  title: {
    /* Phase 9: Premium typography hierarchy with Plus Jakarta Sans */
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    fontFamily: 'var(--font-family)',
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: 'var(--letter-spacing-tight-medium, -0.02em)',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-2xl)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    padding: 'var(--spacing-1)',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneButton: {
    // FIX: Mobile-only "Done" button for unmistakable exit affordance
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-brand-primary, #2563eb)',
    cursor: 'pointer',
    padding: 'var(--spacing-2) var(--spacing-3)',
    lineHeight: 1,
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Mobile redesign: tab bar
  mobileTabBar: {
    display: 'flex',
    gap: 0,
    flexShrink: 0,
    borderBottom: '1px solid var(--color-border-light)',
    backgroundColor: 'var(--color-bg-secondary)',
    padding: '0 var(--spacing-4)',
  },
  mobileTab: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    minHeight: '44px',
  },
  mobileTabActive: {
    color: 'var(--color-brand-primary, #2563eb)',
    borderBottomColor: 'var(--color-brand-primary, #2563eb)',
  },
  // Mobile Summary tab: 3 hero cards
  mobileSummaryCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-3)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  mobileSummaryCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: 'var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border-light)',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  mobileSummaryCardLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
    fontWeight: 'var(--font-weight-medium)',
  },
  mobileSummaryCardValue: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-1)',
    lineHeight: 1.2,
  },
  mobileSummaryCardWhy: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    lineHeight: 1.4,
  },
  // Mobile Breakdown tab: section blocks
  mobileBreakdownSection: {
    marginBottom: 'var(--spacing-5)',
  },
  mobileBreakdownSectionTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-2)',
  },
  mobileBreakdownBlock: {
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-3)',
    border: '1px solid var(--color-border-light)',
  },
  embedContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    overflowX: 'hidden',
    padding: 'var(--spacing-4) var(--spacing-5)',
    paddingBottom: 'calc(var(--spacing-4) + env(safe-area-inset-bottom, 0))',
    WebkitOverflowScrolling: 'touch',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    overflowX: 'hidden', // FULLSCREEN: Prevent horizontal scroll (critical)
    overflowY: 'auto', // Explicit vertical scrolling
    padding: 'var(--spacing-4) var(--spacing-5)',
    paddingBottom: 'calc(var(--spacing-4) + env(safe-area-inset-bottom, 0))', // FULLSCREEN: Safe area for iOS home indicator
    WebkitOverflowScrolling: 'touch', // FULLSCREEN: Smooth iOS scrolling (critical for iOS Safari)
    overscrollBehavior: 'contain', // FULLSCREEN: Prevent scroll chaining to background
    overscrollBehaviorY: 'contain', // Explicitly prevent vertical scroll chaining
    overscrollBehaviorX: 'none', // Prevent horizontal overscroll
    position: 'relative',
    minHeight: 0, // FULLSCREEN: Critical for flex child scrolling (allows shrink below content size)
    maxWidth: '100%', // Prevent content from exceeding container
    width: '100%', // Full width
    boxSizing: 'border-box', // Include padding in width calculation
    wordWrap: 'break-word', // Break long words to prevent overflow
    overflowWrap: 'break-word', // Modern word breaking
    // Ensure content can scroll independently
    '-webkit-overflow-scrolling': 'touch', // Additional iOS support (safari)
  },
  scrollFadeTop: {
    position: 'sticky',
    top: 0,
    height: '8px',
    background: 'linear-gradient(to bottom, var(--color-bg-primary), transparent)',
    pointerEvents: 'none',
    zIndex: 1,
    marginTop: '-8px',
    marginBottom: 'var(--spacing-2)',
  },
  summarySection: {
    marginBottom: 'var(--spacing-5)',
    paddingBottom: 'var(--spacing-4)',
    borderBottom: '1px solid var(--color-border-light)',
  },
  summaryTitle: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    margin: '0 0 var(--spacing-3) 0',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-2) 0',
  },
  summaryLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },
  summaryValue: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  whySection: {
    marginTop: 'var(--spacing-3)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
  },
  whyItem: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-2)',
    lineHeight: 1.5,
  },
  accordionSection: {
    marginBottom: 'var(--spacing-2)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    /* Phase 9: Brand-tinted shadow for accordion elevation */
    boxShadow: '0 1px 2px rgba(37, 72, 96, 0.08)',
  },
  accordionHeader: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: '44px',
  },
  accordionTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
  },
  accordionIcon: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    transition: 'transform 0.2s ease-out',
  },
  accordionContent: {
    padding: 'var(--spacing-3) var(--spacing-4)',
    backgroundColor: 'var(--color-bg-primary)',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 'var(--spacing-3) 0', /* Phase 9: Increased spacing for calm feel */
    borderBottom: '1px solid rgba(37, 72, 96, 0.08)', /* Phase 9: Brand-tinted divider */
    width: '100%', // Full width
    maxWidth: '100%', // Prevent overflow
    boxSizing: 'border-box',
    gap: 'var(--spacing-2)', // Add gap for label/value separation
    flexWrap: 'wrap', // Wrap on very small screens
  },
  detailLabel: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    flex: '0 1 auto', // Shrink if needed but don't grow
    minWidth: 0, // Allow shrinking below content size
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
  },
  detailValue: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    flex: '0 1 auto', // Shrink if needed but don't grow
    minWidth: 0, // Allow shrinking below content size
    textAlign: 'right', // Right-align values
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'nowrap', // Prevent currency values from wrapping mid-value
    overflow: 'hidden',
    textOverflow: 'ellipsis', // Show ellipsis if value is too long
  },
  breakdownSection: {
    marginTop: 'var(--spacing-2)',
  },
  emptyState: {
    padding: 'var(--spacing-6)',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  // PREMIUM: Trust-grade partial data notice
  partialNotice: {
    marginBottom: 'var(--spacing-4)',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
  },
  partialNoticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    marginBottom: 'var(--spacing-2)',
  },
  partialNoticeIcon: {
    fontSize: 'var(--font-size-base)',
  },
  partialNoticeTitle: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
  },
  partialNoticeText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    margin: '0 0 var(--spacing-2) 0',
    lineHeight: 1.5,
  },
  retryButton: {
    padding: 'var(--spacing-2) var(--spacing-3)',
    backgroundColor: 'var(--color-brand-primary, #2563eb)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    minHeight: '44px',
    transition: 'background-color 120ms ease-out',
  },
  // PREMIUM: Summary-first responsive grid (stacks on mobile to prevent horizontal overflow)
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr', // Mobile-first: single column to prevent horizontal scroll
    gap: 'var(--spacing-3)',
    marginBottom: 'var(--spacing-5)',
    paddingBottom: 'var(--spacing-4)',
    borderBottom: '1px solid var(--color-border-light)',
    width: '100%', // Full width
    maxWidth: '100%', // Prevent overflow
    boxSizing: 'border-box', // Include padding in width
    // Note: Desktop can override with media query if needed, but mobile-first prevents overflow
  },
  summaryGridItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: 'var(--spacing-3)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-md)',
    minHeight: '100px',
    width: '100%', // Full width of grid cell
    maxWidth: '100%', // Prevent overflow
    boxSizing: 'border-box', // Include padding in width
    wordWrap: 'break-word', // Break long values
    overflowWrap: 'break-word', // Modern word breaking
  },
  summaryGridLabel: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
    fontWeight: 'var(--font-weight-medium)',
  },
  summaryGridValue: {
    fontSize: 'var(--font-size-lg)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-1)',
    lineHeight: 1.2,
    wordWrap: 'break-word', // Break long currency values if needed
    overflowWrap: 'break-word',
    whiteSpace: 'nowrap', // Keep currency values on one line
    overflow: 'hidden',
    textOverflow: 'ellipsis', // Show ellipsis if value is too long
  },
  summaryGridWhy: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-tertiary)',
    lineHeight: 1.4,
    marginTop: 'auto',
  },
};
